import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FixedNav, Caption, Card, Button, PulseDot } from '@/ui';
import { practiceApi } from './api';
import { CADENCE_TIMING } from './types';
import type { CreateGameParams, PracticeCadence, PracticeColor, PracticeTheme } from './types';

/**
 * Practice Arena Setup — sélecteur niveau / cadence / couleur / thématique
 * avant de lancer une partie classique (Salve P1 T3).
 *
 * UX :
 *  - Slider ELO 800 → 2500 par paliers de 200 (9 valeurs : 800/1000/1200/
 *    1400/1500/1800/2100/2300/2500). Label dynamique selon palier
 *    (Débutant/Intermédiaire/Avancé/Expert).
 *  - 5 cards cadence cliquables (bullet/blitz3+2/blitz5+3/rapid/classique).
 *  - 3 cards couleur (Blancs / Noirs / Aléatoire).
 *  - Carousel thématique (1 standard + 5 thèmes seed depuis API).
 *  - Bouton "Lancer la partie" → POST /practice/games → navigate game/:id.
 */

const ELO_LEVELS = [800, 1000, 1200, 1400, 1500, 1800, 2100, 2300, 2500] as const;

const LEVEL_LABEL = (level: number): string => {
  if (level < 1200) return 'Débutant';
  if (level < 1700) return 'Intermédiaire';
  if (level < 2200) return 'Avancé';
  return 'Expert';
};

const CADENCES: PracticeCadence[] = ['bullet', 'blitz_3_2', 'blitz_5_3', 'rapid_15_10', 'classique_30_0'];
const COLORS: { value: PracticeColor; label: string; emoji: string }[] = [
  { value: 'white', label: 'Blancs', emoji: '♔' },
  { value: 'black', label: 'Noirs', emoji: '♚' },
  { value: 'random', label: 'Aléatoire', emoji: '🎲' },
];

export function PracticeSetupPage(): JSX.Element {
  const navigate = useNavigate();
  const [levelElo, setLevelElo] = useState<number>(1500);
  const [cadence, setCadence] = useState<PracticeCadence>('rapid_15_10');
  const [color, setColor] = useState<PracticeColor>('white');
  const [themeSlug, setThemeSlug] = useState<string>('standard');

  const themesQ = useQuery({
    queryKey: ['practice', 'themes'],
    queryFn: () => practiceApi.listThemes(),
  });

  const createMutation = useMutation({
    mutationFn: (params: CreateGameParams) => practiceApi.createGame(params),
    onSuccess: (data) => {
      navigate(`/practice-arena/game/${data.id}`);
    },
  });

  const launch = (): void => {
    createMutation.mutate({
      level_elo: levelElo,
      cadence,
      color,
      theme_slug: themeSlug === 'standard' ? null : themeSlug,
    });
  };

  return (
    <div className="min-h-screen bg-ink-900 text-paper-50">
      <FixedNav />
      <main className="pt-20 pb-12 px-4 md:px-8 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <PulseDot color="var(--orange-500)" />
          <Caption color="orange">Setup — Nouvelle partie</Caption>
        </div>
        <h1 className="text-heading-1 font-display font-semibold mb-1">Configure ton match</h1>
        <p className="text-ink-300 text-body-lg italic mb-8">
          Choisis l'adversaire, la cadence, la couleur, le départ.
        </p>

        {/* Section Niveau */}
        <Card accent="orange" className="mb-6">
          <Caption color="orange">Niveau adversaire</Caption>
          <div className="mt-3 mb-2 flex items-baseline gap-4">
            <span className="text-display-2 font-display tabular-nums" data-testid="level-display">
              {levelElo}
            </span>
            <span className="text-body-lg text-ink-300">{LEVEL_LABEL(levelElo)}</span>
          </div>
          <input
            type="range"
            min={0}
            max={ELO_LEVELS.length - 1}
            step={1}
            value={ELO_LEVELS.indexOf(levelElo as (typeof ELO_LEVELS)[number])}
            onChange={(e) => setLevelElo(ELO_LEVELS[Number(e.target.value)] ?? 1500)}
            className="w-full accent-orange-500"
            data-testid="level-slider"
            aria-label="Niveau ELO de Stockfish"
            title="Niveau ELO de Stockfish"
          />
          <div className="flex justify-between text-caption text-ink-300 mt-1 tabular-nums">
            <span>800</span>
            <span>1500</span>
            <span>2500</span>
          </div>
        </Card>

        {/* Section Cadence */}
        <Card accent="orange" className="mb-6">
          <Caption color="orange">Cadence</Caption>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="cadence-grid">
            {CADENCES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCadence(c)}
                className={`px-3 py-2 rounded-lg border transition-colors text-body-sm font-medium ${
                  cadence === c
                    ? 'border-orange-500 bg-orange-500/10 text-orange-500'
                    : 'border-ink-700 text-ink-300 hover:border-ink-600'
                }`}
                data-testid={`cadence-${c}`}
              >
                {CADENCE_TIMING[c].label}
              </button>
            ))}
          </div>
        </Card>

        {/* Section Couleur */}
        <Card accent="orange" className="mb-6">
          <Caption color="orange">Couleur</Caption>
          <div className="mt-3 grid grid-cols-3 gap-2" data-testid="color-grid">
            {COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setColor(c.value)}
                className={`px-3 py-3 rounded-lg border transition-colors flex flex-col items-center gap-1 ${
                  color === c.value
                    ? 'border-orange-500 bg-orange-500/10 text-orange-500'
                    : 'border-ink-700 text-ink-300 hover:border-ink-600'
                }`}
                data-testid={`color-${c.value}`}
              >
                <span className="text-2xl">{c.emoji}</span>
                <span className="text-caption">{c.label}</span>
              </button>
            ))}
          </div>
        </Card>

        {/* Section Thématique */}
        <Card accent="orange" className="mb-8">
          <Caption color="orange">Thématique de départ</Caption>
          {themesQ.isLoading && <p className="text-ink-300 mt-3">Chargement…</p>}
          {themesQ.isError && <p className="text-coral-500 mt-3">Erreur de chargement des thèmes.</p>}
          {themesQ.data && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="theme-grid">
              {themesQ.data.themes.map((t) => (
                <ThemeCard
                  key={t.slug}
                  theme={t}
                  selected={themeSlug === t.slug}
                  onSelect={() => setThemeSlug(t.slug)}
                />
              ))}
            </div>
          )}
        </Card>

        {/* Boutons d'action */}
        <div className="flex flex-col md:flex-row gap-3">
          <Button
            variant="primary"
            accent="orange"
            size="hero"
            onClick={launch}
            disabled={createMutation.isPending}
            data-testid="launch-btn"
          >
            {createMutation.isPending ? 'Lancement…' : '🚀 Lancer la partie'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/practice-arena')}>
            Annuler
          </Button>
        </div>
        {createMutation.isError && (
          <p className="mt-3 text-coral-500 text-body-sm" data-testid="create-error">
            Impossible de créer la partie. Réessaie.
          </p>
        )}
      </main>
    </div>
  );
}

function ThemeCard({
  theme,
  selected,
  onSelect,
}: {
  theme: PracticeTheme;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left px-3 py-2 rounded-lg border transition-colors ${
        selected
          ? 'border-orange-500 bg-orange-500/10'
          : 'border-ink-700 hover:border-ink-600'
      }`}
      data-testid={`theme-${theme.slug}`}
    >
      <div className={`text-body-sm font-medium ${selected ? 'text-orange-500' : 'text-paper-50'}`}>
        {theme.name_fr}
      </div>
      <div className="text-caption text-ink-300 mt-0.5">{theme.description_fr}</div>
    </button>
  );
}
